// src/pages/krebsgesamt/RegionalMapChoropleth.js
import React, { useEffect, useState, useMemo } from "react";
import kreisData from "../../assets/data/kreismap.json";
import colorBins from "../../assets/data/fixed_color_bins.json";
import { Container, Row, Col, Form } from "react-bootstrap";
import { geoPath, geoMercator } from "d3-geo";
import { scaleLinear } from "d3-scale";

const MAP_HEIGHT = 600;
const LEGEND_TICK_TARGET = 19;

const geoJsonPath = `${process.env.PUBLIC_URL}/data/sachsen_anhalt_kreise.geojson`;

const projection = geoMercator()
  .center([11.692889, 51.95034])
  .scale(12000)
  .translate([400, 400]);

const pathGenerator = geoPath().projection(projection);


const binsSorted = [...colorBins].sort((a, b) => a.min - b.min);
const domainBreaks = binsSorted.map((b) => b.min);
const rangeColors = binsSorted.map((b) => b.color);
const colorScale = scaleLinear().domain(domainBreaks).range(rangeColors).clamp(true);
const getColorForValue = (value) => colorScale(Number(value) || 0);


function pickTicksFromBreaks(bins, maxVal, targetCount = 8) {
  const values = [...bins.map((b) => b.min), maxVal];
  if (values.length <= targetCount) return values;
  const step = (values.length - 1) / (targetCount - 1);
  const kept = [];
  for (let i = 0; i < targetCount; i++) kept.push(values[Math.round(i * step)]);
  return kept.filter((v, i) => kept.indexOf(v) === i);
}


const ColorLegend = ({ hoveredValue, legendHeight = MAP_HEIGHT }) => {
  const min = binsSorted[0].min;
  const lastBin = binsSorted[binsSorted.length - 1];
  const max = Number.isFinite(lastBin.max) ? lastBin.max : lastBin.min;
  const n = binsSorted.length;

  const uniformStops = binsSorted.map((b, i) => `${b.color} ${(i / n) * 100}%`);
  uniformStops.push(`${binsSorted[n - 1].color} 100%`);

  const barWidth = 22;
  const barHeight = legendHeight;

  const tickVals = pickTicksFromBreaks(binsSorted, max, LEGEND_TICK_TARGET);

  const clamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));
  const [legendHover, setLegendHover] = useState(null);
  const activeValueRaw = legendHover ?? (Number.isFinite(hoveredValue) ? hoveredValue : null);
  const activeValue = activeValueRaw == null ? null : clamp(activeValueRaw, min, max);

  const segments = binsSorted.map((b, i) => {
    const lo = b.min;
    const hi = i === n - 1 ? max : b.max;
    return [lo, hi];
  });

  function valueToUniformPct(value) {
    const v = clamp(value ?? min, min, max);
    if (v >= max) return 100;
    let s = n - 1;
    for (let i = 0; i < n; i++) {
      const [lo, hi] = segments[i];
      if (v >= lo && v < hi) {
        s = i;
        break;
      }
    }
    const [lo, hi] = segments[s];
    const t = (v - lo) / (hi - lo || 1);
    const pct = ((s + t) / n) * 100;
    return clamp(pct, 0, 100);
  }

  function yToValueFromUniform(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = clamp((rect.bottom - e.clientY) / rect.height, 0, 1);
    const pos = ratio * n;
    const s = Math.min(n - 1, Math.max(0, Math.floor(pos)));
    const t = Math.min(1, Math.max(0, pos - s));
    const [lo, hi] = segments[s];
    return lo + t * (hi - lo);
  }

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
      <div
        role="slider"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={activeValue ?? undefined}
        style={{
          width: barWidth,
          height: barHeight,
          background: `linear-gradient(to top, ${uniformStops.join(", ")})`,
          border: "1px solid #ccc",
          borderRadius: 4,
          boxShadow: "inset 0 0 2px rgba(0,0,0,0.15)",
          position: "relative",
          cursor: "crosshair",
        }}
        onMouseMove={(e) => setLegendHover(yToValueFromUniform(e))}
        onMouseLeave={() => setLegendHover(null)}
      >
        {activeValue != null && (
          <>
            <div
              style={{
                position: "absolute",
                bottom: `calc(${valueToUniformPct(activeValue)}% - 1px)`,
                left: -4,
                width: barWidth + 8,
                height: 2,
                background: "#222",
                opacity: 0.9,
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: `calc(${valueToUniformPct(activeValue)}% - 10px)`,
                left: barWidth + 8,
                background: "rgba(0,0,0,0.85)",
                color: "#fff",
                fontSize: 11,
                padding: "2px 6px",
                borderRadius: 4,
                whiteSpace: "nowrap",
                pointerEvents: "none",
              }}
            >
              {activeValue.toFixed(1).replace(".", ",")}
            </div>
          </>
        )}
      </div>

      <div style={{ position: "relative", height: barHeight, width: 64, fontSize: 12 }}>
        {tickVals.map((v, i) => {
          const idx = v === max ? n : binsSorted.findIndex((b) => b.min === v);
          const pct = ((idx < 0 ? 0 : idx) / n) * 100;
          const posPx = (pct / 100) * barHeight;
          return (
            <div
              key={`${v}-${i}`}
              style={{
                position: "absolute",
                bottom: posPx - 8,
                display: "flex",
                alignItems: "center",
                gap: 6,
                whiteSpace: "nowrap",
                color: "#333",
              }}
            >
              <div style={{ width: 10, height: 1, background: "#333" }} />
              <div>{v}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const RegionalMapChoropleth = () => {
  const [geoJson, setGeoJson] = useState(null);
  const [year, setYear] = useState("2023");
  const [selectedEntit, setSelectedEntit] = useState("Krebs gesamt ohne C44");
  const [hoveredDistrict, setHoveredDistrict] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    fetch(geoJsonPath)
      .then((res) => res.json())
      .then((data) => setGeoJson(data))
      .catch((err) => console.error("GeoJSON load error:", err));
  }, []);

  const availableYears = useMemo(
    () => [...new Set(kreisData.map((d) => d.diayear))].sort((a, b) => Number(a) - Number(b)),
    []
  );

  // Keep entit names as-is, ensure “Krebs gesamt ohne C44” always first
  const entitOptions = useMemo(() => {
    const unique = [...new Set(kreisData.map((d) => d.entit))];
    const sorted = unique.sort((a, b) => a.localeCompare(b, "de"));
    const krebsIndex = sorted.indexOf("Krebs gesamt ohne C44");
    if (krebsIndex > 0) {
      sorted.splice(krebsIndex, 1);
      sorted.unshift("Krebs gesamt ohne C44");
    }
    return sorted.map((entit) => ({ entit, label: entit }));
  }, []);

  const filteredData = useMemo(() => {
    const filtered = kreisData.filter(
      (d) => String(d.diayear) === String(year) && d.entit === selectedEntit
    );
    return {
      Männer: Object.fromEntries(
        filtered.filter((d) => d.SEX === "M").map((d) => [d.region, d.altersInz])
      ),
      Frauen: Object.fromEntries(
        filtered.filter((d) => d.SEX === "W").map((d) => [d.region, d.altersInz])
      ),
    };
  }, [year, selectedEntit]);

  const renderMap = (gender) => {
    if (!geoJson) return <p>Lade Karte...</p>;
    const selected = filteredData[gender];
    return (
      <svg width="100%" height={MAP_HEIGHT} viewBox="0 0 800 800">
        {geoJson.features.map((feature, index) => {
          const regionName = feature.properties.GEN;
          const value = selected[regionName] || 0;
          const pathD = pathGenerator(feature);
          const isHovered = hoveredDistrict?.name === regionName;
          return (
            <path
              key={index}
              d={pathD}
              fill={getColorForValue(value)}
              stroke={isHovered ? "black" : "white"}
              strokeWidth={isHovered ? 2 : 0.5}
              style={{ transition: "stroke 0.3s ease, stroke-width 0.3s ease" }}
              onMouseEnter={(e) => {
                setHoveredDistrict({ name: regionName, value });
                setMousePosition({ x: e.clientX, y: e.clientY });
              }}
              onMouseMove={(e) => setMousePosition({ x: e.clientX, y: e.clientY })}
              onMouseLeave={() => setHoveredDistrict(null)}
            />
          );
        })}
      </svg>
    );
  };

  return (
    <Container className="mt-4">
      <h2 className="section-title">Regionale Verteilung</h2>
      <div className="section-title-underline"></div>

      <div className="aktuelles-text" style={{ marginTop: "0.5rem" }}>
        <p>
          Das Land Sachsen-Anhalt besteht aus drei kreisfreien Städten (Dessau-Roßlau, Halle (Saale) und Magdeburg) und elf Landkreisen.
          Die jüngste Bevölkerung Sachsen-Anhalts lebte 2022 mit einem Durchschnittsalter von 44 Jahren in Halle (Saale), 
          während die älteste 2022 im Landkreis Mansfeld-Südharz mit 50 Jahren wohnte <strong>[1]</strong>. 
          Um die unterschiedliche Größe und Altersverteilung von Städten und Landkreisen bei Krebsneuerkrankungen zu berücksichtigen, werden im Folgenden altersstandardisierte Inzidenzraten pro 100 000 Einwohner berechnet. 
          Die Ergebnisse können für Krebs gesamt (ohne C44) oder für einzelne Entitäten angezeigt werden.
        </p>
        <p>
          Bei der Interpretation der Inzidenzraten ist zu beachten, das regionale Unterschiede auch meldungsbedingt sein können.
          Insbesondere bei seltenen Krebserkrankungen können fehlende Meldungen scheinbare Inzidenzunterschiede erklären.
        </p>
      </div>


      <Row className="mb-4">
        <Col xs={12} md={6}>
          <Form.Group controlId="entitSelect">
            <Form.Label>Krebs-Auswahl:</Form.Label>
            <Form.Select
              value={selectedEntit}
              onChange={(e) => setSelectedEntit(e.target.value)}
            >
              {entitOptions.map(({ entit, label }) => (
                <option key={entit} value={entit}>
                  {label}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={12} md={4}>
          <Form.Group controlId="yearSelect">
            <Form.Label>Jahr auswählen:</Form.Label>
            <Form.Select value={year} onChange={(e) => setYear(e.target.value)}>
              {availableYears.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))} 
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col md={5}>
          <h5 className="text-center">Männer</h5>
          {renderMap("Männer")}
        </Col>
        <Col md={5}>
          <h5 className="text-center">Frauen</h5>
          {renderMap("Frauen")}
        </Col>
        <Col md={2} className="d-flex align-items-start justify-content-center">
          <ColorLegend hoveredValue={hoveredDistrict?.value ?? null} legendHeight={MAP_HEIGHT} />
        </Col>
      </Row>

      {hoveredDistrict && (
        <div
          style={{
            position: "absolute",
            left: mousePosition.x + 10,
            top: mousePosition.y + 10,
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            color: "white",
            padding: "8px 12px",
            borderRadius: "6px",
            fontSize: "14px",
            pointerEvents: "none",
            zIndex: 9999,
          }}
        >
          <strong>{hoveredDistrict.name}</strong>
          <br />
         ASR Inzidenz: {hoveredDistrict.value.toFixed(2).replace(".", ",")} / 100.000
        </div>
      )}


{/* ---------------- Referenzen ---------------- */}
      <div className="mt-4 pt-3 border-top" style={{ fontSize: "0.95rem" }}>
        <h6>Referenzen</h6>
        <p className="mb-1">
          <strong>[1]</strong> Pressemitteilung: Menschen in Sachsen-Anhalt waren 2022 im Durchschnitt 47 Jahre und 11 Monate alt. Statistisches Landesamt Sachsen-Anhalt.
           https://statistik.sachsen-anhalt.de/fileadmin/Bibliothek/Landesaemter/StaLa/startseite/Daten_und_Veroeffentlichungen/Pressemitteilungen/2023/g_Juli/212-Durchschnittsalter-2022.pdf
        </p>
      </div>
    </Container>
  );
};

export default RegionalMapChoropleth;


